"use client"

import { useEffect } from "react"
import { useUbPDPState } from "@/store/ubPDPState"

import { DEFAULT_MARKET_CHANNEL } from "@/lib/market/market-channel"
import type { PdpContext } from "@/lib/storefront/product/pdp-service"
import type { VariationProductData } from "@/lib/storefront/product/product"

type ProductVariantsResponse = {
  variants?: VariationProductData[]
}

const variantIndexPromiseCache = new Map<string, Promise<ProductVariantsResponse | undefined>>()

function loadVariantIndex(productId: string, sbLocale: string, channel: string) {
  const cacheKey = `${sbLocale}:${channel}:${productId}`
  let promise = variantIndexPromiseCache.get(cacheKey)

  if (!promise) {
    const searchParams = new URLSearchParams()
    searchParams.set("sbLocale", sbLocale)
    searchParams.set("channel", channel)

    promise = fetch(`/api/storefront/product-variants/${encodeURIComponent(productId)}?${searchParams.toString()}`, {
      cache: "no-store",
      credentials: "same-origin",
    }).then((response) => (response.ok ? response.json() : undefined))
    variantIndexPromiseCache.set(cacheKey, promise)
  }

  return promise
}

export default function ProductDetailVariantIndexLoader({ pdpContext }: { pdpContext: PdpContext }) {
  const masterProduct = pdpContext.productAndArticle.masterProduct
  const setVariantIndex = useUbPDPState((state) => state.setVariantIndex)
  const storedVariants = useUbPDPState((state) =>
    masterProduct?.id ? state.variantIndexes[masterProduct.id] : undefined
  )

  useEffect(() => {
    if (!masterProduct?.id || masterProduct.variants?.length || storedVariants?.length) {
      return
    }

    let active = true
    const channel = pdpContext.channel ?? DEFAULT_MARKET_CHANNEL

    loadVariantIndex(masterProduct.id, pdpContext.sbLocale, channel)
      .then((result) => {
        if (active && result?.variants?.length) {
          setVariantIndex(masterProduct.id, result.variants)
        }
      })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [
    masterProduct?.id,
    masterProduct?.variants?.length,
    pdpContext.channel,
    pdpContext.sbLocale,
    setVariantIndex,
    storedVariants?.length,
  ])

  return null
}
